import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, Send, Bot, User, Copy, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Message {
  id: number;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

const AIChat = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "Halo! Saya adalah AI assistant untuk membantu Anda belajar matematika perkalian. Apa yang ingin Anda tanyakan?",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const sendMessageToAI = async (message: string) => {
    try {
      const response = await fetch(`https://text.pollinations.ai/${encodeURIComponent(message)}`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error('Failed to get response from AI');
      }
      
      const aiResponse = await response.text();
      return aiResponse;
    } catch (error) {
      console.error('Error calling AI API:', error);
      return "Maaf, saya mengalami kesulitan untuk merespons saat ini. Silakan coba lagi nanti.";
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: messages.length + 1,
      text: inputMessage,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      // Add context about multiplication to the prompt
      const contextualPrompt = `Sebagai guru matematika yang ahli dalam perkalian, tolong jawab pertanyaan berikut dengan penjelasan yang mudah dipahami dan berikan contoh jika diperlukan. Pertanyaan: ${inputMessage}`;
      
      const aiResponse = await sendMessageToAI(contextualPrompt);
      
      const aiMessage: Message = {
        id: messages.length + 2,
        text: aiResponse,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal mendapatkan respons dari AI. Silakan coba lagi.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const suggestedQuestions = [
    "Bagaimana cara mengalikan bilangan dua digit?",
    "Apa itu tabel perkalian dan bagaimana menghafalnya?",
    "Jelaskan metode perkalian lattice",
    "Bagaimana cara mengalikan pecahan?",
    "Apa perbedaan perkalian dan penjumlahan?",
    "Berikan tips menghafal tabel perkalian"
  ];

  const handleSuggestedQuestion = (question: string) => {
    setInputMessage(question);
  };

  const handleCopyMessage = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Berhasil!",
        description: "Pesan telah disalin ke clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal menyalin pesan",
        variant: "destructive",
      });
    }
  };

  const handleShareMessage = async (text: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Jawaban AI Matematika",
          text: text,
        });
      } catch (error) {
        console.log("Error sharing:", error);
      }
    } else {
      // Fallback to copy
      handleCopyMessage(text);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Tanya AI Matematika
          </CardTitle>
          <CardDescription>
            Bertanya kepada AI tentang perkalian, matematika, dan konsep-konsep terkait
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Chat Messages */}
            <Card className="h-96">
              <ScrollArea className="h-full p-4">
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          message.isUser
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {!message.isUser && <Bot className="w-4 h-4 mt-1 flex-shrink-0" />}
                          <div className="flex-1">
                            <div className="text-sm">{message.text}</div>
                            <div className="flex items-center justify-between mt-2">
                              <div className="text-xs opacity-70">
                                {formatTime(message.timestamp)}
                              </div>
                              {!message.isUser && (
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCopyMessage(message.text)}
                                    className="h-6 px-2 text-xs"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleShareMessage(message.text)}
                                    className="h-6 px-2 text-xs"
                                  >
                                    <Share2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                          {message.isUser && <User className="w-4 h-4 mt-1 flex-shrink-0" />}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="max-w-[80%] rounded-lg p-3 bg-muted">
                        <div className="flex items-center gap-2">
                          <Bot className="w-4 h-4" />
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>

            {/* Input Area */}
            <div className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ketik pertanyaan Anda tentang matematika..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button 
                onClick={handleSendMessage} 
                disabled={!inputMessage.trim() || isLoading}
                size="icon"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>

            {/* Suggested Questions */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">
                Pertanyaan yang sering ditanyakan:
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {suggestedQuestions.map((question, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSuggestedQuestion(question)}
                    className="text-left justify-start text-wrap h-auto py-2"
                    disabled={isLoading}
                  >
                    {question}
                  </Button>
                ))}
              </div>
            </div>

            {/* Tips */}
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <div className="text-sm space-y-2">
                  <div className="font-medium">💡 Tips bertanya:</div>
                  <ul className="text-muted-foreground space-y-1 text-xs">
                    <li>• Berikan konteks yang jelas tentang apa yang ingin Anda pelajari</li>
                    <li>• Minta contoh konkret untuk pemahaman yang lebih baik</li>
                    <li>• Tanyakan tentang metode alternatif untuk menyelesaikan masalah</li>
                    <li>• Jangan ragu untuk meminta penjelasan lebih detail</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIChat;